var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { Menu } from '@mui/material';
import { More16 } from '@yunex/yds-icons';
import React, { forwardRef, Fragment, useEffect, useRef, useState, } from 'react';
import Button from '../Button';
import NestedMenuItem from '../NestedMenuItem';
import { Alignment } from './_definitions/ActionGroupDefinitions';
import { rem } from '../../utils/rem';
import { resolveSxAsArray } from '../../utils/utils';
export var MENU_ID = 'open-menu';
var CONTEXT_MENU_MAX_WIDTH = rem(320);
var CONTEXT_MENU_MIN_WIDTH = rem(160);
var DEFAULT_MENU_ALIGNMENT = (Alignment === null || Alignment === void 0 ? void 0 : Alignment.RIGHT) || 'right'; // || is fix for dev env where Alignment is undefined because of the enum declaration.
var styles = {
    menu: {
        '& .MuiFormControl-root': {
            flex: 1,
            '& [class*=MuiTextField-root]': {
                width: 1,
            },
        },
        '& .MuiFormHelperText-root': {
            whiteSpace: 'normal',
        },
    },
};
export var OverflowMenu = forwardRef(function (_a, ref) {
    var _b, _c, _d, _e, _f;
    var hiddenElements = _a.hiddenElements, MenuProps = _a.MenuProps, setAnchorEl = _a.setAnchorEl, anchorEl = _a.anchorEl;
    var _g = MenuProps !== null && MenuProps !== void 0 ? MenuProps : {}, menuButtonProps = _g.menuButtonProps, restMenuProps = __rest(_g, ["menuButtonProps"]);
    var menuRef = useRef(null);
    var _h = useState(true), firstRender = _h[0], setFirstRender = _h[1];
    // Handle focus manually, because autoFocus is set to false.
    useEffect(function () {
        var _a, _b;
        (_b = (_a = menuRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('ul')) === null || _b === void 0 ? void 0 : _b.focus();
    }, [anchorEl]);
    // return focus to the button when we transitioned from !open -> open
    useEffect(function () {
        var _a;
        if (!anchorEl && ref && 'current' in ref && !firstRender) {
            (_a = ref === null || ref === void 0 ? void 0 : ref.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
        setFirstRender(false);
    }, [anchorEl]);
    var handleMenuButtonClick = function (event) {
        var _a;
        setAnchorEl(event.currentTarget);
        (_a = menuButtonProps === null || menuButtonProps === void 0 ? void 0 : menuButtonProps.onClick) === null || _a === void 0 ? void 0 : _a.call(menuButtonProps, event);
    };
    var handleMenuButtonClose = function () {
        setAnchorEl(null);
    };
    // Early exit.
    if (!(hiddenElements === null || hiddenElements === void 0 ? void 0 : hiddenElements.length)) {
        return null;
    }
    // Checks if any element of the menu has selected property set to true. This can be used to style the menu button later.
    var hasSelected = hiddenElements.some(function (el) { return el === null || el === void 0 ? void 0 : el.props.selected; });
    return (React.createElement(Fragment, { key: "overflow-menu-wrapper" },
        React.createElement(Button, __assign({ "aria-controls": "overflow-menu", "aria-haspopup": "true", "aria-label": MENU_ID, id: MENU_ID, color: "tertiary", "data-component": "action-group-menu-button" }, menuButtonProps, { "data-has-selected": hasSelected, icon: true, key: "overflow-menu-button", onClick: handleMenuButtonClick, ref: ref }), (_b = restMenuProps === null || restMenuProps === void 0 ? void 0 : restMenuProps.icon) !== null && _b !== void 0 ? _b : React.createElement(More16, null)),
        React.createElement(Menu, __assign({ anchorEl: anchorEl, 
            // Due to technical limitations we have to set the `autoFocus` to `false` in case the first element is a `NestedMenuItem` component.
            autoFocus: hiddenElements[0].type.displayName !==
                NestedMenuItem.displayName, anchorOrigin: {
                vertical: 'bottom',
                horizontal: (_c = MenuProps === null || MenuProps === void 0 ? void 0 : MenuProps.dropdownMenuAlignment) !== null && _c !== void 0 ? _c : DEFAULT_MENU_ALIGNMENT,
            }, className: MenuProps === null || MenuProps === void 0 ? void 0 : MenuProps.className, id: "overflow-menu", 
            // With false will not re-render when items are hiding or showing in the action group.
            keepMounted: false, ref: menuRef, onClose: handleMenuButtonClose, open: Boolean(anchorEl), "data-component": "action-group-menu" }, restMenuProps, { MenuListProps: __assign({ sx: {
                    minWidth: (_d = restMenuProps === null || restMenuProps === void 0 ? void 0 : restMenuProps.minWidth) !== null && _d !== void 0 ? _d : CONTEXT_MENU_MIN_WIDTH,
                    maxWidth: (_e = restMenuProps === null || restMenuProps === void 0 ? void 0 : restMenuProps.maxWidth) !== null && _e !== void 0 ? _e : CONTEXT_MENU_MAX_WIDTH,
                } }, restMenuProps === null || restMenuProps === void 0 ? void 0 : restMenuProps.MenuListProps), sx: __spreadArray([styles.menu], resolveSxAsArray(restMenuProps === null || restMenuProps === void 0 ? void 0 : restMenuProps.sx), true), transformOrigin: __assign({ vertical: 'top', horizontal: (_f = restMenuProps === null || restMenuProps === void 0 ? void 0 : restMenuProps.dropdownMenuAlignment) !== null && _f !== void 0 ? _f : DEFAULT_MENU_ALIGNMENT }, restMenuProps === null || restMenuProps === void 0 ? void 0 : restMenuProps.transformOrigin) }), hiddenElements)));
});
OverflowMenu.displayName = 'OverflowMenu';
